import React, { useEffect, useRef, useMemo, useCallback } from "react";
import {
  $createParagraphNode,
  $createTextNode,
  $getRoot,
  $getSelection,
  $isTextNode,
  TextNode,
} from "lexical";
import { $generateNodesFromDOM } from "@lexical/html";
import InputEditor from "../../RichTextEditor/Editor/inputEditor";
import { SharedHistoryContext } from "../../RichTextEditor/LexicalEditor/context/SharedHistoryContext";
import { TableContext } from "../../RichTextEditor/LexicalEditor/plugins/TablePlugin";
import { ToolbarContext } from "../../RichTextEditor/LexicalEditor/context/ToolbarContext";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";

export default function ColorTextEditor2({
  setFieldState,
  fieldName,
  prevContent,
  setFormState,
}) {
  const [editor] = useLexicalComposerContext();
  
  const handleEditorFocus = useCallback(() => {
    if (setFieldState && editor) {
      setFieldState({
        fieldName,
        editorId: `${fieldName}_${Date.now()}`,
        editor: editor
      });
    }
  }, [editor, fieldName, setFieldState]);

  const handleSelectionChange = useCallback(() => {
    if (setFieldState && editor) {
      setFieldState(prev => ({
        ...prev,
        editor: editor
      }));
    }
  }, [editor, setFieldState]);

  useEffect(() => {
    if (!editor) return;

    // Register selection change listener
    const unregisterSelectionListener = editor.registerUpdateListener(({ editorState }) => {
      handleSelectionChange();
    });

    // Register focus listener
    const removeRootListener = editor.registerRootListener((rootElement, prevRootElement) => {
      if (rootElement) {
        rootElement.addEventListener('focus', handleEditorFocus);
        rootElement.addEventListener('click', handleEditorFocus);
      }
      if (prevRootElement) {
        prevRootElement.removeEventListener('focus', handleEditorFocus);
        prevRootElement.removeEventListener('click', handleEditorFocus);
      }
    });

    return () => {
      unregisterSelectionListener();
      removeRootListener();
    };
  }, [editor, handleEditorFocus, handleSelectionChange]);

  const createEditorId = (rowType, rowId, fieldName) => {
    return `${rowType}_${rowId || 'new'}_${fieldName}`;
  };

  const editorId = createEditorId(prevContent, setFieldState, fieldName);
  
  // Create a stable key that doesn't change on every render
  const stableKey = useMemo(() => editorId, [editorId]);


// Initialize content if prevContent is provided
useEffect(() => {
  if (editor && prevContent !== undefined) {
    // Only initialize if the editor is empty or if this is the first load
    editor.update(() => {
      const root = $getRoot();
      const children = root.getChildren();
      
      // Only initialize if the editor is empty
      if (children.length === 0 || (children.length === 1 && children[0].getTextContent() === '')) {
        root.clear();
        
        // Always create a default paragraph node
        const paragraph = $createParagraphNode();
        root.append(paragraph);
        
        // If we have content, try to parse it safely
        if (prevContent && prevContent.trim() !== '') {
          try {
            const parser = new DOMParser();
            
            // Prepare the content for parsing
            let contentToParse = prevContent.trim();
            
            // Check if content starts with a span (inline element) and wrap it in a paragraph
            if (contentToParse.startsWith('<span')) {
              contentToParse = `<p>${contentToParse}</p>`;
            }
            // Check if content doesn't start with a block element, wrap it
            else if (!contentToParse.match(/^<(p|div|h[1-6]|ul|ol|li|blockquote)/i)) {
              contentToParse = `<p>${contentToParse}</p>`;
            }
            
            const dom = parser.parseFromString(contentToParse, "text/html");
            const nodes = $generateNodesFromDOM(editor, dom, {
              preserveWhitespace: true,
            });
            
            // Only append if we have valid nodes
            const validNodes = nodes.filter(node => 
              node !== null && 
              node !== undefined && 
              node.getType && 
              typeof node.getType === 'function'
            );
            
            if (validNodes.length > 0) {
              // Clear and append valid nodes
              root.clear();
              root.append(...validNodes);
            }
          } catch (error) {
            console.error('Error parsing content:', error);
            // Fallback: try to create a text node with the content
            try {
              root.clear();
              const paragraph = $createParagraphNode();
              const textNode = $createTextNode(prevContent.replace(/<[^>]*>/g, '')); // Strip HTML tags as fallback
              paragraph.append(textNode);
              root.append(paragraph);
            } catch (fallbackError) {
              console.error('Fallback parsing also failed:', fallbackError);
              // Keep the default empty paragraph
              const paragraph = $createParagraphNode();
              root.append(paragraph);
            }
          }
        }
      }
    });
  }
}, [editor]); // Only depend on editor, not prevContent
// Add focus listener in useEffect
useEffect(() => {
  if (!editor) return;

  const removeListener = editor.registerRootListener((rootElement, prevRootElement) => {
    if (rootElement) {
      rootElement.addEventListener('focus', handleEditorFocus);
    }
    if (prevRootElement) {
      prevRootElement.removeEventListener('focus', handleEditorFocus);
    }
    
    return () => {
      if (rootElement) {
        rootElement.removeEventListener('focus', handleEditorFocus);
      }
    };
  });

  return removeListener;
}, [editor, handleEditorFocus]);

const getTextFromHTML = (htmlContent) => {
  if (!htmlContent) return '';
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = htmlContent;
  return tempDiv.textContent || tempDiv.innerText || '';
};


 const handleEditorChange = (content) => {
    // Only update if content has actually changed
    setFormState((prev) => {
      const currentContent = prev[fieldName];      
      if (currentContent !== content) {
        const textContent = getTextFromHTML(content);
        
        // Add data-text attribute to the content HTML
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = content;
        
        // Add data-text attribute to the root element or wrap in span
        if (tempDiv.firstElementChild) {
          tempDiv.firstElementChild.setAttribute('data-text', textContent);
        } else {
          // If no HTML elements, wrap in span
          tempDiv.innerHTML = `<span data-text="${textContent}">${content}</span>`;
        }
        
        const modifiedContent = tempDiv.innerHTML;
        
        return { ...prev, [fieldName]: modifiedContent };
      }
      return prev;
    });
  };

  return (
    <div style={{ padding: "4px" }}>
      <SharedHistoryContext>
        <TableContext>
          <ToolbarContext>
            <div className={`editor-shell`}>
              <InputEditor
                editorId={editorId}
                key={stableKey}
                editor={editor}
                setFieldState={setFieldState}
                handleEditorChange={handleEditorChange}
              />
            </div>
          </ToolbarContext>
        </TableContext>
      </SharedHistoryContext>
    </div>
  );
}
